import { ComponentIds, PartPair, ReplaceAssemblyPartUtils } from '@inter-ikea-core/core';
import { PlannerEcsSystem, ProductType } from '@inter-ikea-storageone/range';
const MIDDLE_PANNEL = '90584724';
const LEFT_PANNEL = '50597295--left';
const RIGHT_PANNEL = '50597295--right';
/**
 * System for handling panel snapping & middle panel sharing
 */
export class PanelSnapSystem extends PlannerEcsSystem {
    constructor(coreApi) {
        super(coreApi, 'PanelSnapSystem', ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID);
        /**
         * Replace middle panel with right panel
         */
        this.replaceMiddlePanel = (entity, panels) => {
            if (panels.includes(MIDDLE_PANNEL)) {
                ReplaceAssemblyPartUtils.replaceProductParts(entity, [new PartPair('right-panel', RIGHT_PANNEL)], this.coreApi.getServices());
            }
        };
        /**
         * Add left panel to a section if missing
         */
        this.addLeftPanelIfMissing = (entity, panels) => {
            if (!panels.includes(LEFT_PANNEL)) {
                ReplaceAssemblyPartUtils.replaceProductParts(entity, [new PartPair('left-panel', LEFT_PANNEL)], this.coreApi.getServices());
            }
        };
        /**
         * Add right panel to a section if missing
         */
        this.addRightPanelIfMissing = (entity, panels) => {
            if (!panels.includes(RIGHT_PANNEL)) {
                ReplaceAssemblyPartUtils.replaceProductParts(entity, [new PartPair('right-panel', RIGHT_PANNEL)], this.coreApi.getServices());
            }
        };
        this.placementService = coreApi.placementService;
    }
    onAdd(entity) {
        if (this.isEnabled && entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
            this.shareMiddleShelfIfAlginedWithOtherFrames(entity);
        }
    }
    onUpdate(entity) {
        if (this.isEnabled && entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
            this.shareMiddleShelfIfAlginedWithOtherFrames(entity);
        }
    }
    onRemove(entity) {
        if (this.isEnabled && entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
            // If frame was removed from scene, we must iterate through sections and fill empty left panels and replace unused middle panels
            // Since the section's shared panels might have been changed
            this.fillEmptyLeftPanels();
            this.replaceUnusedMiddlePanels();
        }
    }
    /**
     * Check if frame has aligned frames and if so handle shared middle panel
     */
    shareMiddleShelfIfAlginedWithOtherFrames(frame) {
        const { right: rightEntity, left: leftEntity } = this.coreApi.entities.getAlignedFrames(frame, 'align-left', 'align-right', true, true);
        if (!leftEntity && !rightEntity) {
            const panels = this.getPanelsOnFrame(frame);
            const hasMiddlePanel = panels.includes(MIDDLE_PANNEL);
            const hasLeftPanel = panels.includes(LEFT_PANNEL);
            if (hasMiddlePanel) {
                // Middle was shared with another frame, find that frame and add a left panel
                this.replaceMiddlePanel(frame, panels);
                this.fillEmptyLeftPanels();
            }
            else if (!hasLeftPanel) {
                // This frame has neither mid or left panel, which means it was part of a shared middle panel
                // We must add a left panel to this frame, and find the pairing section and replace middle panel
                this.addLeftPanelIfMissing(frame, this.getPanelsOnFrame(frame));
                this.replaceUnusedMiddlePanels();
            }
        }
        else {
            // Two frames are aligned - remove overlapping panels with middle panel
            this.addMiddlePanel(frame, leftEntity, rightEntity);
            // If frame previously was part of a shared middle panel, there might be a scenario with fauly panels elsewhere
            // We must iterate through sections ad fill empty left panels and replace unused middle panels
            this.fillEmptyLeftPanels();
            this.replaceUnusedMiddlePanels();
        }
    }
    /**
     * Add middle panel to frame
     */
    addMiddlePanel(frame, leftEntity, rightEntity) {
        const noPanelRef = '';
        if (leftEntity) {
            // frame => left panel, replace with nothing
            ReplaceAssemblyPartUtils.replaceProductParts(frame, [new PartPair('left-panel', noPanelRef)], this.coreApi.getServices());
            // left entity => right panel replace with middle
            ReplaceAssemblyPartUtils.replaceProductParts(leftEntity, [new PartPair('right-panel', MIDDLE_PANNEL)], this.coreApi.getServices());
        }
        if (rightEntity) {
            // right entity => left panel, replace with nothing
            ReplaceAssemblyPartUtils.replaceProductParts(rightEntity, [new PartPair('left-panel', noPanelRef)], this.coreApi.getServices());
            // frame => right panel replace with middle
            ReplaceAssemblyPartUtils.replaceProductParts(frame, [new PartPair('right-panel', MIDDLE_PANNEL)], this.coreApi.getServices());
        }
    }
    /**
     * Gets all panels on a frame
     */
    getPanelsOnFrame(entity) {
        const panelsOnFrame = this.coreApi.entities
            .getChildrenWithOneOfProductTypes(entity, [ProductType.LADMAKARE_PANEL])
            ?.map(f => f.getComponent(ComponentIds.PRODUCT_COMPONENT_ID)?.productRef)
            .filter(panel => panel !== undefined);
        return panelsOnFrame;
    }
    /**
     * Iterates through frames in scene and adds left panel to those missing it
     */
    fillEmptyLeftPanels() {
        const framesInScene = this.coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
        framesInScene.forEach(frame => {
            const panels = this.getPanelsOnFrame(frame);
            const hasLeftPanel = panels.includes(LEFT_PANNEL);
            const { left: leftEntity } = this.coreApi.entities.getAlignedFrames(frame, 'align-left', 'align-right', true, true);
            if (!hasLeftPanel && !leftEntity) {
                this.addLeftPanelIfMissing(frame, this.getPanelsOnFrame(frame));
            }
        });
    }
    /**
     * Iterates through frames in scene and replace mid panel if it no longer shared
     */
    replaceUnusedMiddlePanels() {
        const framesInScene = this.coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
        framesInScene.forEach(frame => {
            const panels = this.getPanelsOnFrame(frame);
            const hasMiddlePanel = panels.includes(MIDDLE_PANNEL);
            const { right: rightEntity } = this.coreApi.entities.getAlignedFrames(frame, 'align-left', 'align-right', true, true);
            if (hasMiddlePanel && !rightEntity) {
                this.replaceMiddlePanel(frame, this.getPanelsOnFrame(frame));
            }
        });
    }
}
//# sourceMappingURL=PanelSnapSystem.js.map